import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Play, Sparkles, FileCheck, Zap, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import DashboardMockup from "./DashboardMockup";
import Counter from "./Counter";
import { PrimaryButton, GhostButton, EASE } from "./shared";

const HERO_LINES = [
  { text: "AI Clinical Intelligence", gradient: false },
  { text: "for Orthodontics", gradient: true },
];

function MaskedLine({ text, delay, gradient }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 0.95, ease: EASE, delay }}
        className={`block ${
          gradient ? "text-transparent bg-clip-text bg-gradient-to-r from-[#2563EB] to-[#6366F1]" : ""
        }`}
      >
        {text}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 45, damping: 20 });
  const sy = useSpring(my, { stiffness: 45, damping: 20 });
  const mockX = useTransform(sx, (v) => v * 18);
  const mockY = useTransform(sy, (v) => v * 14);
  const orbX = useTransform(sx, (v) => v * -40);

  const onMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      onMouseMove={onMouseMove}
      className="relative overflow-hidden pt-32 pb-16 lg:pt-44 lg:pb-28"
    >
      {/* Background decor */}
      <motion.div style={{ x: orbX }} className="absolute -top-40 -right-40 w-[34rem] h-[34rem] rounded-full bg-[#2563EB]/10 blur-[110px] pointer-events-none" />
      <div className="absolute top-1/3 -left-48 w-[30rem] h-[30rem] rounded-full bg-[#6366F1]/10 blur-[110px] pointer-events-none" />
      <div
        className="absolute inset-0 opacity-[0.35] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(15,23,42,0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(15,23,42,0.045) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
          maskImage: "radial-gradient(ellipse 80% 60% at 50% 0%, black, transparent)",
          WebkitMaskImage: "radial-gradient(ellipse 80% 60% at 50% 0%, black, transparent)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-14 lg:gap-10 items-center">
        {/* Copy */}
        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="inline-flex items-center gap-2 rounded-full border border-[#2563EB]/20 bg-[#2563EB]/5 px-4 py-1.5 mb-8"
            data-testid="hero-badge"
          >
            <Sparkles className="w-3.5 h-3.5 text-[#2563EB]" strokeWidth={2} />
            <span className="text-xs font-semibold text-[#2563EB]">Early access now open</span>
          </motion.div>

          <h1 className="font-display font-medium text-4xl sm:text-5xl lg:text-6xl tracking-[-0.025em] leading-[1.05] text-[#0F172A]">
            {HERO_LINES.map((line, i) => (
              <MaskedLine key={line.text} text={line.text} gradient={line.gradient} delay={0.15 + i * 0.14} />
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.6 }}
            className="text-base md:text-lg text-slate-500 leading-relaxed mt-7 max-w-md"
            data-testid="hero-subtitle"
          >
            AI-assisted progress monitoring, clinical review support, and treatment tracking
            for orthodontic practices — with the doctor always in the loop.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.75 }}
            className="flex flex-wrap items-center gap-4 mt-9"
          >
            <PrimaryButton
              testId="hero-book-demo-button"
              onClick={() => toast.success("Demo request received — our team will reach out shortly.")}
            >
              Book a Demo
            </PrimaryButton>
            <GhostButton testId="hero-watch-demo-button" href="#preview">
              <Play className="w-4 h-4 text-[#2563EB]" strokeWidth={2} />
              Watch Product Demo
            </GhostButton>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-10"
            data-testid="hero-trust-row"
          >
            {["Doctor-in-the-loop AI", "Orthodontic progress monitoring", "Treatment tracking & insights"].map((item) => (
              <span key={item} className="flex items-center gap-1.5 text-xs font-medium text-slate-400">
                <ShieldCheck className="w-3.5 h-3.5 text-[#2563EB]/70" strokeWidth={2} />
                {item}
              </span>
            ))}
          </motion.div>
        </div>

        {/* Dashboard mockup */}
        <div className="lg:col-span-7 relative">
          <motion.div
            initial={{ opacity: 0, y: 48, rotateX: 8 }}
            animate={{ opacity: 1, y: 0, rotateX: 0 }}
            transition={{ duration: 1.1, ease: EASE, delay: 0.35 }}
            style={{ x: mockX, y: mockY, transformPerspective: 1200 }}
          >
            <DashboardMockup />
          </motion.div>

          {/* Floating: note drafted */}
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, ease: EASE, delay: 1.1 }}
            className="absolute -top-6 -right-3 sm:-right-6"
          >
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="flex items-center gap-3 rounded-2xl border border-slate-200/70 bg-white/85 backdrop-blur-xl px-4 py-3 shadow-[0_20px_50px_rgba(15,23,42,0.15)]"
              data-testid="float-note-drafted"
            >
              <span className="w-8 h-8 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                <FileCheck className="w-4 h-4 text-emerald-500" strokeWidth={2} />
              </span>
              <div>
                <p className="text-xs font-semibold text-[#0F172A]">AI note drafted</p>
                <p className="text-[10px] text-slate-400">Approved in 12 seconds</p>
              </div>
            </motion.div>
          </motion.div>

          {/* Floating: time saved */}
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, ease: EASE, delay: 1.3 }}
            className="absolute -bottom-8 -left-3 sm:-left-8"
          >
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              className="rounded-2xl border border-slate-200/70 bg-white/85 backdrop-blur-xl px-5 py-4 shadow-[0_20px_50px_rgba(15,23,42,0.15)]"
              data-testid="float-time-saved"
            >
              <div className="flex items-center gap-2">
                <Zap className="w-3.5 h-3.5 text-[#6366F1]" strokeWidth={2} />
                <p className="text-[10px] font-semibold tracking-[0.18em] uppercase text-slate-400">
                  Time saved today
                </p>
              </div>
              <p className="font-display font-semibold text-3xl text-[#0F172A] tracking-tight mt-1.5">
                <Counter to={58} />
                <span className="text-[#2563EB] text-xl"> min</span>
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
